import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleApiError } from "../../../Utils/thunkHelpers"
import { serializeData } from "../../../Utils/utils"
import {
  addCreditAutomationComment as addCreditAutomationCommentApi,
  exportCreditAutomation as exportCreditAutomationApi,
  getCreditAutomationQueue,
  getCreditAutomationStatusOptions,
  updateCreditAutomationStatus
} from "../services"
import { CREDITS_AUTOMATION_SLICE_NAME } from "./creditsAutomation.constants"

// CHA1-2308 (chargeback_back, see CREDIT_AUTOMATION_FRONTEND.md section 5). This queue never
// returns "Not requested" rows (nothing for an admin to do there yet), so that status isn't an
// option here.
export const ACCESS_REQUEST_STATUSES = ["Pending", "Action needed", "Active", "Lost", "Disabled"]

export const getCreditsAutomationList = createAsyncThunk(
  `${CREDITS_AUTOMATION_SLICE_NAME}/get_list`,
  async (
    { page = 1, page_size = 20, ordering, filters = {} } = {},
    { dispatch, rejectWithValue }
  ) => {
    const response = await getCreditAutomationQueue({
      page,
      page_size,
      ordering,
      ...serializeData(filters)
    })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// CHA1-3066: real, deployed on dev-5. The response shape from update-status isn't fully confirmed
// yet, so this builds the row update from what we know we just sent (guaranteed correct for the
// core fields) and lets the real response override matching keys if it returns the full row.
export const updateAccessRequestStatus = createAsyncThunk(
  `${CREDITS_AUTOMATION_SLICE_NAME}/update_status`,
  async ({ id, status, failure_reason, failure_details }, { dispatch, rejectWithValue }) => {
    const response = await updateCreditAutomationStatus({
      id,
      status,
      failure_reason,
      failure_details
    })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return {
      id,
      access_request_status: status,
      action_needed_reason: failure_reason ?? null,
      action_needed_details: failure_details ?? null,
      access_update_date: new Date().toISOString(),
      ...response.data
    }
  }
)
export const addCreditAutomationComment = createAsyncThunk(
  `${CREDITS_AUTOMATION_SLICE_NAME}/add_comment`,
  async ({ id, comment }, { dispatch, rejectWithValue }) => {
    const response = await addCreditAutomationCommentApi({ id, comment })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { id, comments: comment, ...response.data }
  }
)

export const exportCreditsAutomation = createAsyncThunk(
  `${CREDITS_AUTOMATION_SLICE_NAME}/export`,
  async ({ export_type, filters = {} }, { rejectWithValue }) => {
    const response = await exportCreditAutomationApi({
      export_type,
      ...(export_type === "as_filtered" ? serializeData(filters) : {})
    })
    if (!response.ok) {
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getStatusOptions = createAsyncThunk(
  `${CREDITS_AUTOMATION_SLICE_NAME}/get_status_options`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await getCreditAutomationStatusOptions()
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)
